import React, { useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Alert } from "@mui/material";
import {
  ChangeCmpMemberMembershipMutation,
  CmpInvoiceStatusEnum,
  CmpMemberDetailFieldsFragment,
  CmpMemberMembershipChangeFieldsInput,
  CmpMemberMembershipChangeOutcomeEnum,
  CmpMemberRenewalInvoiceDispositionEnum,
  CmpMemberRenewalStepStateEnum,
  GetCmpMemberDetailDocument,
  GetCmpMemberDetailQuery,
  GetCmpMemberDetailQueryVariables,
  GetUserQuery,
  useApproveCmpMemberRenewalInvoiceDraftMutation,
  useCancelCmpMemberPendingMembershipChangeMutation,
  useChangeCmpMemberMembershipMutation,
  useCreateCmpMemberRenewalInvoiceMutation,
  useDownloadCmpInvoicePdfMutation,
  useSendCmpInvoicePaymentRequestMutation,
  useUpdateCmpMemberPendingMembershipChangeMutation,
} from "../../../../graphql/types";
import { PRESERVE_ERROR_FOR_CALLER_CONTEXT } from "../../../../utils/apolloErrorPolicy";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../../utils/userFacingError";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import { applyCmpOrganizationNoun } from "../../../../utils/cmpOrganizationNoun";
import DesignDialog, { DesignDialogButton } from "../../../common/dashboard/DesignDialog";
import { SectionShell } from "../memberWorkflowShared";
import { downloadBase64File } from "../../Invoicing/invoiceFileDownloads";
import { getOutboundEmailFailureMessage, wasOutboundEmailDeliveryConfirmed } from "../../Invoicing/invoiceDeliveryFeedback";
import { InvoiceDraftDocumentData } from "../../Invoicing/ManageInvoices/ManageInvoiceActionModals";
import { refreshAfterCommittedMemberAction } from "../memberInvoiceDelivery";
import MemberRenewalInvoicePreview from "./MemberRenewalInvoicePreview";
import {
  getLatestMemberRenewalDetail,
  getMemberRenewalPresentation,
  hasSameMemberRenewalQuote,
  getMemberRenewalRevision,
  isMemberRenewalPreviewCurrent,
  MemberRenewalPreview,
} from "./memberRenewalScenario";

type RenewalResult = ChangeCmpMemberMembershipMutation["changeCmpMemberMembership"];
type Action = "draft" | "approve" | "update" | "cancelPending";
type Props = {
  detail: CmpMemberDetailFieldsFragment;
  chamber?: GetUserQuery["getUser"]["chamberInfo"];
  timeZone?: string | null;
  taxLabel: string;
  taxRegistrationNumber?: string | null;
  manualPaymentMethods: InvoiceDraftDocumentData["manualPaymentMethods"];
  draftFingerprint: string;
  formError: string;
  /** Step 5 auto-renew checkbox; the design labels the button from it. */
  autoRenewSelected: boolean;
  getMembershipInput: () => CmpMemberMembershipChangeFieldsInput | null;
  renderFields: (previewTotal: string | undefined, disabled: boolean) => React.ReactNode;
  createOperationId: () => string;
  onCompleted: (detail: CmpMemberDetailFieldsFragment) => Promise<unknown>;
  onBusyChange: (busy: boolean) => void;
  onBeforePersist: () => Promise<{ detail: CmpMemberDetailFieldsFragment; consentSaved: boolean }>;
};

// Shared plain-language text (server text without the Apollo wrapper, a
// network message, a UserFacingError, or the task fallback).
const RENEWAL_NOT_CONFIRMED_TEXT = `We could not confirm that the renewal was saved. Refresh the page to check before you try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;
// availability.reason is server text and shows first when the server sends it.
const RENEWAL_ACTION_UNAVAILABLE_TEXT = `You cannot do this renewal step right now. Refresh the page and try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;
const errorText = (error: unknown, fallback: string = RENEWAL_NOT_CONFIRMED_TEXT) =>
  getUserFacingErrorText(error, fallback);

const MembershipRenewalStep = ({
  detail: queriedDetail, chamber, timeZone, taxLabel, taxRegistrationNumber, manualPaymentMethods,
  draftFingerprint, formError, autoRenewSelected, getMembershipInput, renderFields, createOperationId,
  onCompleted, onBusyChange, onBeforePersist,
}: Props) => {
  const confirmedDetailRef = useRef<CmpMemberDetailFieldsFragment | null>(null);
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const restoreResultFocusRef = useRef(false);
  const detail = getLatestMemberRenewalDetail(queriedDetail, confirmedDetailRef.current);
  const client = useApolloClient();
  const [createInvoice] = useCreateCmpMemberRenewalInvoiceMutation({ context: PRESERVE_ERROR_FOR_CALLER_CONTEXT });
  const [approveDraft] = useApproveCmpMemberRenewalInvoiceDraftMutation({ context: PRESERVE_ERROR_FOR_CALLER_CONTEXT });
  const [changeMembership] = useChangeCmpMemberMembershipMutation({ context: PRESERVE_ERROR_FOR_CALLER_CONTEXT });
  const [updatePending] = useUpdateCmpMemberPendingMembershipChangeMutation({ context: PRESERVE_ERROR_FOR_CALLER_CONTEXT });
  const [cancelPending] = useCancelCmpMemberPendingMembershipChangeMutation({ context: PRESERVE_ERROR_FOR_CALLER_CONTEXT });
  const [downloadPdf] = useDownloadCmpInvoicePdfMutation({ context: PRESERVE_ERROR_FOR_CALLER_CONTEXT });
  const [sendInvoice] = useSendCmpInvoicePaymentRequestMutation({ context: PRESERVE_ERROR_FOR_CALLER_CONTEXT });
  const noun = useCmpOrganizationNoun();
  const [preview, setPreview] = useState<MemberRenewalPreview | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState<"cancelPending" | "email" | null>(null);
  const [createdInvoice, setCreatedInvoice] = useState<RenewalResult["replacementInvoice"]>(null);
  const [success, setSuccess] = useState<React.ReactNode>(null);
  const [refreshWarning, setRefreshWarning] = useState("");
  const singleFlight = useRef(false);
  const requestSequence = useRef(0);
  const operationIds = useRef(new Map<string, string>());
  const preparedMembership = useRef<CmpMemberMembershipChangeFieldsInput | null>(null);
  const fingerprintRef = useRef(draftFingerprint);
  fingerprintRef.current = draftFingerprint;
  const renewal = detail.renewal;
  const memberRevision = getMemberRenewalRevision(detail);
  const invoiceRevision = renewal?.cycleInvoice?.updatedAtUtcMilli;
  const cycleInvoiceId = renewal?.cycleInvoice?.id;
  useEffect(() => {
    requestSequence.current += 1;
    preparedMembership.current = null;
    setPreview(null);
    setPreviewLoading(false);
  }, [draftFingerprint, memberRevision, invoiceRevision, cycleInvoiceId]);
  useEffect(() => {
    onBusyChange(busy);
    return () => onBusyChange(false);
  }, [busy, onBusyChange]);
  useEffect(() => {
    if (!restoreResultFocusRef.current || busy || previewOpen || confirmation || createdInvoice || success || error) return;
    restoreResultFocusRef.current = false;
    const action = primaryActionRef.current;
    const target = action?.isConnected && !action.disabled && renewal?.stepState !== CmpMemberRenewalStepStateEnum.InvoiceGenerated
      ? action : stepHeadingRef.current;
    target?.isConnected && target.focus({ preventScroll: true });
  }, [busy, previewOpen, confirmation, createdInvoice, success, error, renewal?.stepState]);
  const closeResult = () => {
    restoreResultFocusRef.current = true;
    setCreatedInvoice(null);
    setSuccess(null);
  };
  useEffect(() => () => { requestSequence.current += 1; }, []);
  if (!renewal) {
    return <Alert severity="warning">We could not load the renewal details. Refresh the page to try again.</Alert>;
  }
  const presentation = getMemberRenewalPresentation({
    renewal, autoRenewSelected, firstMembership: !detail.member.cmpExtension?.membership,
  });
  const cycleInvoice = renewal.cycleInvoice;
  const pending = renewal.pendingMembershipChange;
  const memberName = detail.member.name;
  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    timeZone: normalizeCmpTimeZone(timeZone).timeZone,
  });
  const formatDate = (value?: number | null) => value ? dateFormatter.format(value) : "-";
  const money = (amount: number) => new Intl.NumberFormat("en-CA", {
    style: "currency", currency: preview?.membership.pricingSnapshot.currency || detail.member.cmpExtension?.membership?.pricingSnapshot.currency || "CAD",
  }).format(amount / 100);
  const getOperationId = (action: string, input: unknown) => {
    const key = JSON.stringify({ action, input });
    const existing = operationIds.current.get(key);
    if (existing) return existing;
    const id = createOperationId();
    operationIds.current.set(key, id);
    return id;
  };
  const baseInput = {
    chamberId: detail.member.chamberId,
    memberId: detail.member.id,
    expectedMemberUpdatedAtUtcMilli: memberRevision,
  };
  const fetchPreview = async (source: CmpMemberDetailFieldsFragment, membership: CmpMemberMembershipChangeFieldsInput) => {
    const response = await client.query<GetCmpMemberDetailQuery, GetCmpMemberDetailQueryVariables>({
        query: GetCmpMemberDetailDocument,
        fetchPolicy: "no-cache",
        context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
        variables: { input: {
          chamberId: source.member.chamberId,
          memberId: source.member.id,
          renewalPreview: {
            membership,
            expectedMemberUpdatedAtUtcMilli: getMemberRenewalRevision(source),
            expectedCurrentInvoiceId: source.member.cmpExtension?.latestInvoiceId,
            existingDraftInvoiceId: source.renewal?.cycleInvoice?.invoiceStatus === CmpInvoiceStatusEnum.Draft ? source.renewal!.cycleInvoice!.id : undefined,
            expectedInvoiceUpdatedAtUtcMilli: source.renewal?.cycleInvoice?.invoiceStatus === CmpInvoiceStatusEnum.Draft ? source.renewal!.cycleInvoice!.updatedAtUtcMilli : undefined,
          },
        } },
      });
    const next = response.data.getCmpMemberDetail.renewalPreview;
    if (!next || !next.invoice.billingSnapshot || !next.invoice.lineItems || !next.invoice.invoiceKind || next.invoice.subtotalAmountInCents == null || next.invoice.taxAmountInCents == null || next.invoice.totalAmountInCents == null || !isMemberRenewalPreviewCurrent({ detail: source, preview: next })) {
      throw new UserFacingError("This member or its draft renewal invoice was changed. Refresh the page, then open the preview again.");
    }
    return next;
  };
  const openPreview = async () => {
    if (singleFlight.current || !presentation.primaryAvailable) return;
    const membership = getMembershipInput();
    if (!membership) return;
    const sequence = ++requestSequence.current;
    const requestedFingerprint = draftFingerprint;
    setPreview(null);
    preparedMembership.current = null;
    setError("");
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const next = await fetchPreview(detail, membership);
      if (sequence !== requestSequence.current || requestedFingerprint !== fingerprintRef.current) return;
      preparedMembership.current = membership;
      setPreview(next);
    } catch (failure) {
      if (sequence === requestSequence.current) {
        setPreview(null);
        setError(errorText(failure, buildStaffErrorFallback("load the renewal invoice preview")));
      }
    } finally {
      if (sequence === requestSequence.current) setPreviewLoading(false);
    }
  };
  const runAction = async (action: Action) => {
    if (singleFlight.current) return;
    const availability = action === "draft" ? renewal.actions.saveDraft
      : action === "approve" ? (cycleInvoice ? renewal.actions.approveDraft : renewal.actions.createNow)
        : action === "cancelPending" ? renewal.actions.cancelPendingChange : renewal.actions.updateDetails;
    if (!availability.available) { setError(availability.reason || RENEWAL_ACTION_UNAVAILABLE_TEXT); return; }
    const membership = action === "draft" || action === "approve" ? preparedMembership.current
      : action === "update" ? getMembershipInput() : null;
    if ((action === "draft" || action === "approve") &&
      (!preview || !membership || !isMemberRenewalPreviewCurrent({ detail, preview }))) {
      setPreview(null);
      setError("The invoice preview is out of date. Close it and open it again before you select Save Draft or Approve/Create Invoice.");
      return;
    }
    if (action === "update" && !membership) return;
    if (action === "cancelPending" && !pending) return;
    singleFlight.current = true;
    setBusy(true);
    setError("");
    setRefreshWarning("");
    let consentSaved = false;
    try {
      let operationDetail = detail;
      let operationPreview = preview;
      if (action === "draft" || action === "approve" || action === "update") {
        const saved = await onBeforePersist();
        operationDetail = getLatestMemberRenewalDetail(saved.detail, confirmedDetailRef.current);
        confirmedDetailRef.current = operationDetail;
        consentSaved = saved.consentSaved;
        if (consentSaved && preview && membership && action !== "update") {
          operationPreview = await fetchPreview(operationDetail, membership);
          setPreview(operationPreview);
          preparedMembership.current = membership;
          if (!hasSameMemberRenewalQuote(preview, operationPreview)) {
            setError("The email consent was saved, and the invoice changed. Check the new preview, then select Save Draft or Approve/Create Invoice.");
            return;
          }
        }
      }
      const commandBaseInput = { ...baseInput, expectedMemberUpdatedAtUtcMilli: getMemberRenewalRevision(operationDetail) };
      let result: RenewalResult | null | undefined;
      if ((action === "draft" || action === "approve") && membership && operationPreview) {
        if (action === "approve" && operationPreview.existingDraftInvoiceId && operationPreview.invoiceUpdatedAtUtcMilli != null) {
          const input = { ...commandBaseInput, invoiceId: operationPreview.existingDraftInvoiceId, expectedInvoiceUpdatedAtUtcMilli: operationPreview.invoiceUpdatedAtUtcMilli, membership };
          result = (await approveDraft({ variables: { input: { ...input, operationId: getOperationId(action, input) } } })).data?.approveCmpMemberRenewalInvoiceDraft;
        } else {
          const input = {
            ...commandBaseInput, expectedCurrentInvoiceId: operationDetail.member.cmpExtension?.latestInvoiceId,
            existingDraftInvoiceId: operationPreview.existingDraftInvoiceId,
            expectedInvoiceUpdatedAtUtcMilli: operationPreview.invoiceUpdatedAtUtcMilli,
            membership, disposition: action === "draft" ? CmpMemberRenewalInvoiceDispositionEnum.SaveDraft : CmpMemberRenewalInvoiceDispositionEnum.CreateNow,
          };
          result = (await createInvoice({ variables: { input: { ...input, operationId: getOperationId(action, input) } } })).data?.createCmpMemberRenewalInvoice;
        }
      } else if ((action === "update" || action === "cancelPending") && pending) {
        const input = { ...commandBaseInput, expectedPendingOperationId: pending.operationId, expectedPendingVersion: pending.version };
        result = action === "cancelPending"
          ? (await cancelPending({ variables: { input: { ...input, operationId: getOperationId(action, input) } } })).data?.cancelCmpMemberPendingMembershipChange
          : membership ? (await updatePending({ variables: { input: { ...input, membership, operationId: getOperationId(action, { ...input, membership }) } } })).data?.updateCmpMemberPendingMembershipChange : undefined;
      } else if (action === "update" && membership) {
        // Saves details for the upcoming renewal invoice. The server refuses
        // any path that would create, replace or send an invoice now.
        const input = { ...commandBaseInput, expectedCurrentInvoiceId: operationDetail.member.cmpExtension?.latestInvoiceId, membership, renewalDetailsOnly: true };
        result = (await changeMembership({ variables: { input: { ...input, operationId: getOperationId(action, input) } } })).data?.changeCmpMemberMembership;
      }
      if (!result) throw new Error("No renewal response was returned. Refresh the member before trying again.");
      if (!result.changeCompletedSuccessfully || result.outcome === CmpMemberMembershipChangeOutcomeEnum.RequiresManualReview) {
        // result.message is server text for staff and is shown as it is.
        throw new UserFacingError(`${result.message}${result.conflictingInvoiceNumbers.length ? ` These invoices block this change: ${result.conflictingInvoiceNumbers.join(", ")}. Open them in Manage all Invoices & Payments to review them.` : ""}`);
      }
      confirmedDetailRef.current = result.detail;
      setPreviewOpen(false);
      setPreview(null);
      setConfirmation(null);
      // MEMBERSHIP_UPDATED: a free-to-free change applied now; the server text says so.
      if (result.outcome === CmpMemberMembershipChangeOutcomeEnum.NoChange ||
        result.outcome === CmpMemberMembershipChangeOutcomeEnum.MembershipUpdated) setSuccess(result.message);
      else if (action === "draft") setSuccess(<>The Draft membership renewal invoice for <strong>{memberName}</strong> has been saved. To update the invoice status, go to the <strong>Manage all Invoices &amp; Payments</strong> page. To Edit Member Details before updating the invoice status, return to Manage Members step 5.</>);
      else if (action === "approve") setCreatedInvoice(result.replacementInvoice || result.detail.renewal?.cycleInvoice || null);
      else if (action === "cancelPending") setSuccess(<>The scheduled membership change for <strong>{memberName}</strong> has been cancelled.</>);
      else setSuccess(<>The membership renewal details for <strong>{memberName}</strong> have been updated successfully. {autoRenewSelected ? "The auto-renewal invoice" : "The renewal invoice"} will use this information.</>);
      setRefreshWarning(await refreshAfterCommittedMemberAction(() => onCompleted(result!.detail)) || "");
    } catch (failure) {
      setError(`${consentSaved ? "The email consent was saved, but the renewal changes were not saved. " : ""}${errorText(failure)}`);
    } finally { singleFlight.current = false; setBusy(false); }
  };
  const deliverCreatedInvoice = async (kind: "print" | "email") => {
    if (!createdInvoice || singleFlight.current) return;
    singleFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const input = { chamberId: baseInput.chamberId, invoiceId: createdInvoice.id };
      if (kind === "print") {
        const result = (await downloadPdf({ variables: { input } })).data?.downloadCmpInvoicePdf;
        if (!result?.actionCompletedSuccessfully || !result.download) throw new Error("The invoice PDF could not be generated.");
        downloadBase64File(result.download);
        setSuccess(<>The membership renewal invoice for <strong>{memberName}</strong> has been sent to print.</>);
      } else {
        const result = (await sendInvoice({ variables: { input: { ...input, operationId: getOperationId("email", input) } } })).data?.sendCmpInvoicePaymentRequest;
        if (!result || !wasOutboundEmailDeliveryConfirmed(result.actionCompletedSuccessfully, result.outboundEmail)) throw new UserFacingError(getOutboundEmailFailureMessage({ email: result?.outboundEmail, label: "Renewal invoice" }));
        const recipient = result.outboundEmail?.intendedToEmail || result.outboundEmail?.toEmail || createdInvoice.billToSnapshot?.contactEmail || createdInvoice.billingSnapshot?.contactEmail;
        setSuccess(<>The membership renewal invoice for <strong>{memberName}</strong> has been emailed to <strong>{recipient}</strong>.</>);
      }
      setConfirmation(null);
      setCreatedInvoice(null);
    } catch (failure) {
      setError(errorText(failure, buildStaffErrorFallback(kind === "print" ? "create the invoice PDF" : "email the invoice")));
    }
    finally { singleFlight.current = false; setBusy(false); }
  };
  const approveAction = cycleInvoice ? renewal.actions.approveDraft : renewal.actions.createNow;
  const previewTotal = preview && preview.invoice.totalAmountInCents != null && isMemberRenewalPreviewCurrent({ detail, preview })
    ? money(preview.invoice.totalAmountInCents)
    : presentation.locked && cycleInvoice?.totalAmountInCents != null
      ? money(cycleInvoice.totalAmountInCents)
      : undefined;
  return <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
    <div className="flex flex-col gap-[24px] md:flex-row md:justify-between">
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}><h3 ref={stepHeadingRef} tabIndex={-1} style={{ fontSize: 22, fontWeight: 700 }}>{presentation.title}</h3><p style={{ fontSize: 14, whiteSpace: "pre-line" }}>{applyCmpOrganizationNoun(presentation.subtitle, noun)}</p></div>
    </div>
    {pending && <SectionShell id="scheduled-membership-change" variant="design" title="Scheduled Membership Change" contentGap={16}>
      <div className="grid gap-[16px] md:grid-cols-3"><div><p className="text-[14px]">Current Membership</p><strong>{pending.oldMembership.membershipTypeName}</strong></div><div><p className="text-[14px]">Scheduled Membership</p><strong>{pending.newMembership.membershipTypeName}</strong></div><div><p className="text-[14px]">Effective Date</p><strong>{formatDate(pending.effectiveAtUtcMilli)}</strong></div></div>
      <p className="text-[14px]">{pending.status === "ACTION_REQUIRED" ? "Action required" : "Pending"}{pending.actionRequiredReason ? `: ${pending.actionRequiredReason}` : ""}</p><p className="text-[14px]">Renewal pricing will use the {noun.Singular} Set Up in effect when the renewal is generated.</p>
      <button type="button" disabled={busy || !renewal.actions.cancelPendingChange.available} title={renewal.actions.cancelPendingChange.reason || undefined} onClick={() => setConfirmation("cancelPending")} style={{ color: "#F21B1B", fontSize: 13, fontWeight: 600, alignSelf: "flex-start" }}>Cancel Scheduled Change</button>
    </SectionShell>}
    {refreshWarning && <Alert severity="warning">{refreshWarning}</Alert>}
    {presentation.blockedReason && <Alert severity="warning">{presentation.blockedReason}</Alert>}
    {/* A sent renewal locks each billing field (disabled), not the whole block:
        Update Credit Card Information stays active (Design 16, Ajax TEDx Oct 16). */}
    <div>
      {renderFields(previewTotal, busy || presentation.locked)}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, marginTop: 24 }}>
        {formError && <p role="alert" style={{ color: "#E24C4C", fontSize: 13, fontWeight: 600 }}>Complete all required fields (*) before creating the renewal invoice.</p>}
        <button ref={primaryActionRef} type="button" disabled={busy || !presentation.primaryAvailable} onClick={() => void (presentation.sendsInvoice ? openPreview() : runAction("update"))} style={{ color: "white", background: "#E1A731", border: 0, borderRadius: 8, padding: "13px 26px", fontSize: 14, fontWeight: 600, opacity: busy || !presentation.primaryAvailable ? 0.5 : 1 }}>{presentation.primaryLabel}</button>
        {presentation.invoiceCreatedLabel && cycleInvoice && <span style={{ color: "#727272", fontSize: 14 }}>{presentation.invoiceCreatedLabel} {formatDate(renewal.lastRenewalInvoiceCreatedAtUtcMilli || cycleInvoice.createdAtUtcMilli)}</span>}
      </div>
    </div>
    <MemberRenewalInvoicePreview open={previewOpen} loading={previewLoading} busy={busy} preview={preview} error={error} chamber={chamber} timeZone={timeZone} taxLabel={taxLabel} taxRegistrationNumber={taxRegistrationNumber} manualPaymentMethods={manualPaymentMethods} canSaveDraft={renewal.actions.saveDraft.available} canApprove={approveAction.available} saveDraftReason={renewal.actions.saveDraft.reason} approveReason={approveAction.reason} onClose={() => { requestSequence.current += 1; setPreviewLoading(false); setPreviewOpen(false); setError(""); }} onSaveDraft={() => void runAction("draft")} onApprove={() => void runAction("approve")} />
    <DesignDialog open={confirmation === "cancelPending"} busy={busy} onClose={() => setConfirmation(null)} icon="trash" title="Cancel Scheduled Change" message={<>Are you sure you want to cancel the scheduled membership change for <strong>{memberName}</strong>?</>} actions={<><DesignDialogButton tone="red" disabled={busy} onClick={() => void runAction("cancelPending")}>Yes</DesignDialogButton><DesignDialogButton tone="dark" disabled={busy} onClick={() => setConfirmation(null)}>Cancel</DesignDialogButton></>} />
    <DesignDialog open={!!createdInvoice && confirmation !== "email"} busy={busy} onClose={closeResult} icon="success" title="Membership Renewal Invoice Created" message="Membership Renewal Invoice has been generated, would you like to print or email the invoice?" actions={<><DesignDialogButton disabled={busy} onClick={() => void deliverCreatedInvoice("print")}>Print Invoice</DesignDialogButton><DesignDialogButton disabled={busy || createdInvoice?.invoiceStatus !== CmpInvoiceStatusEnum.Unpaid} onClick={() => setConfirmation("email")}>Email Invoice</DesignDialogButton></>} />
    <DesignDialog open={confirmation === "email"} busy={busy} onClose={() => setConfirmation(null)} icon="email" iconColor="#2196F3" title="Email Invoice" message={<>You are about to email the invoice to <strong>{createdInvoice?.billToSnapshot?.customerName || createdInvoice?.billingSnapshot?.customerName}</strong> (<strong>{createdInvoice?.billToSnapshot?.contactEmail || createdInvoice?.billingSnapshot?.contactEmail}</strong>). Are you sure?</>} actions={<><DesignDialogButton tone="blue" disabled={busy} onClick={() => void deliverCreatedInvoice("email")}>Yes, Send</DesignDialogButton><DesignDialogButton tone="dark" disabled={busy} onClick={() => setConfirmation(null)}>Cancel</DesignDialogButton></>} />
    <DesignDialog open={!!error && !previewOpen} onClose={() => setError("")} icon="warning" title="Membership Renewal" message={error} />
    <DesignDialog open={!!success} onClose={closeResult} icon="success" message={success} />
  </div>;
};

export default MembershipRenewalStep;
